/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import SickbayResolution from 'linced-app/enums/sickbay-resolution';
import moment from 'moment';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class RegisterController extends Controller {
  Jurisdiction = Jurisdiction;

  @service
  session;

  @service
  fieldOptions;

  @service
  toaster;

  SickbayResolution = SickbayResolution;

  // Attrs
  // queryParams provides a binding to the routes query params.
  queryParams = ['dateFrom', 'dateTo', 'termId'];

  // State
  // dateFrom provides the stateful binding to a query param.
  @tracked
  dateFrom = moment().format('YYYY-MM-DD');

  // dateTo provides the stateful binding to a query param.
  @tracked
  dateTo = moment().format('YYYY-MM-DD');

  // today provides today's date, for comparison purposes.
  today = moment().format('YYYY-MM-DD');

  @tracked
  filters = [];
  @tracked
  sickbayVisitForDetails = null;
  @tracked
  detailsDialogOpen = false;

  @tracked
  termId;

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed('model.sickbayVisits.[]', 'filters.@each.{field,value}')
  get filteredSickbayVisits() {
    if (isEmpty(this.model.sickbayVisits)) {
      return [];
    }
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model.sickbayVisits;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(filters, 'key', 'yearLevel'),
      this.yearLevelOptions,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.sickbayVisits.filter((row) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'yearLevel':
            return filterValue(
              row.get('learner.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
        }

        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  /**
   * onChangeDateFrom changes the dateFrom field and removes the currently
   * set term as the date has changed, therefore not explicitly the currently
   * set term.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateFrom(newDate) {
    this.dateFrom = newDate;
    this.termId = null;
  }

  /**
   * onChangeDateTo changes the dateTo field and removes the currently set
   * term as the date has changed.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateTo(newDate) {
    this.dateTo = newDate;
    this.termId = null;
  }

  /**
   * setSelectedTermId sets the query params, which in turn will refresh the
   * model.
   *
   * @param {Object} term - A term record.
   */
  @action
  setSelectedTermId(term) {
    this.dateFrom = term.start;
    this.dateTo = term.end;
    this.termId = term.id;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  openDetailsDialog(sickbayVisit) {
    this.sickbayVisitForDetails = sickbayVisit;
    this.detailsDialogOpen = true;
  }

  @action
  closeDetailsDialog() {
    this.sickbayVisitForDetails = null;
    this.detailsDialogOpen = false;
  }
}
